const CURSOR_PROMPT_DEEPLINK_BASE = "cursor://anysphere.cursor-deeplink/prompt";
const CURSOR_PROMPT_DEEPLINK_PREFIX = `${CURSOR_PROMPT_DEEPLINK_BASE}?text=`;
const CURSOR_DEEPLINK_MAX_LENGTH = 8000;
const OG_PILOT_DOCS_MARKDOWN_URL = "https://ogpilot.com/docs.md";
const CURSOR_PROMPT_TRUNCATION_NOTICE =
  "\n\n[Prompt truncated to fit Cursor deeplink length. Use https://ogpilot.com/docs.md for full instructions.]";

export const OG_PILOT_CURSOR_PROMPT = `
# OG Pilot API Integration (SDK-First)

## What OG Pilot Is
OG Pilot is an API and SDK set for generating dynamic Open Graph and Twitter card images for web pages. Your app generates a signed request through an official SDK, receives a CDN image URL, and uses that URL in \`og:image\` and \`twitter:image\` meta tags.

Read and follow the full guide: ${OG_PILOT_DOCS_MARKDOWN_URL}

## Mandatory Rules
- ALWAYS use an official OG Pilot SDK.
- NEVER implement raw JWT signing if an SDK is available.
- NEVER add custom HTTP wrappers or service objects for JWT generation.
- NEVER import JWT libraries for OG Pilot (jsonwebtoken, PyJWT, firebase/php-jwt, etc.) when using an SDK.

## Detect Stack and Install Matching SDK
| Detected file | Stack | Install command |
|---|---|---|
| Gemfile | Ruby / Rails | bundle add og_pilot_ruby && bin/rails g og_pilot_ruby:install |
| package.json | JavaScript / Node | npm install og-pilot-js |
| requirements.txt or pyproject.toml | Python | pip install og-pilot (for Django: pip install og-pilot[django]) |
| composer.json | PHP / Laravel | composer require sunergos/og-pilot-php |

## Integration Workflow
1. Detect language and framework from project files.
2. Install the matching SDK.
3. Add OG_PILOT_API_KEY and OG_PILOT_DOMAIN to environment config.
4. Configure the SDK once at app startup.
5. Generate og:image URLs via SDK helpers (create_image or template-specific helpers).
6. Set both og:image and twitter:image meta tags from generated URL.
7. Keep path handling framework-native (for example request.fullpath in Rails).

## Fallback
If no supported dependency file exists, use the raw API JWT flow from the Authentication section in ${OG_PILOT_DOCS_MARKDOWN_URL}.

## Definition of Done
- SDK is installed and configured.
- Environment variables are wired.
- Meta tags are driven by SDK-generated image URLs.
- No hand-rolled JWT signing code is present.
`.trim();

function truncatePromptToFitDeeplinkLimit(prompt: string, maxLength = CURSOR_DEEPLINK_MAX_LENGTH) {
  const encodedPrompt = encodeURIComponent(prompt);
  if (CURSOR_PROMPT_DEEPLINK_PREFIX.length + encodedPrompt.length <= maxLength) {
    return prompt;
  }

  const encodedNotice = encodeURIComponent(CURSOR_PROMPT_TRUNCATION_NOTICE);
  const availablePromptLength = maxLength - CURSOR_PROMPT_DEEPLINK_PREFIX.length - encodedNotice.length;
  if (availablePromptLength <= 0) {
    return "";
  }

  let low = 0;
  let high = prompt.length;
  let bestFit = "";

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const candidate = prompt.slice(0, mid).trimEnd();
    const encodedCandidateLength = encodeURIComponent(candidate).length;

    if (encodedCandidateLength <= availablePromptLength) {
      bestFit = candidate;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return `${bestFit}${CURSOR_PROMPT_TRUNCATION_NOTICE}`.trimEnd();
}

export function buildCursorPromptLink(prompt: string) {
  const safePrompt = truncatePromptToFitDeeplinkLimit(prompt);
  return `${CURSOR_PROMPT_DEEPLINK_PREFIX}${encodeURIComponent(safePrompt)}`;
}
